import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const NetworkContext = createContext();

// 🆕 دامنه اصلی سرور شما (بدون اسلش آخر)
const SERVER_DOMAIN = 'https://yadoo20.ir';
// ما به مسیر health در سرور شما پینگ می‌زنیم
const PING_URL = `${SERVER_DOMAIN}/api/__health`;

export const NetworkProvider = ({ children }) => {
  // حالت اولیه true است تا هنگام باز شدن برنامه، پیام قطعی به صورت الکی نمایش داده نشود
  const [isOnline, setIsOnline] = useState(true); 
  const [isChecking, setIsChecking] = useState(false);
  const wasOffline = useRef(false);

  const checkRealConnection = useCallback(async () => {
    // ۱. اگر کاربر اصلا دیتا یا وای‌فای گوشی را خاموش کرده باشد
    if (!navigator.onLine) {
      setIsOnline(false);
      wasOffline.current = true;
      return;
    }
    
    setIsChecking(true);

    // تابع کمکی برای پینگ زدن با تایم‌اوت طولانی (مناسب اینترنت ضعیف)
    const performPing = async () => {
      const controller = new AbortController();
      // 🆕 در اینترنت ضعیف (حتی ۵ کیلوبایت) تا ۲۰ ثانیه صبر میکنیم تا جواب سرور برسد
      const timeoutId = setTimeout(() => controller.abort(), 20000); 
      
      try {
        const res = await fetch(`${PING_URL}?t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        clearTimeout(timeoutId);
        return res.ok; // اگر سرور جواب 200 بدهد یعنی متصل است
      } catch (error) {
        clearTimeout(timeoutId);
        return false; // خطا یا تایم‌اوت
      }
    };

    // ==========================================
    // تلاش اول
    // ==========================================
    let isReachable = await performPing();

    // ==========================================
    // 🆕 منطق ۵ ثانیه‌ای هوشمند شما
    // ==========================================
    if (!isReachable) {
      console.log('⚠️ تلاش اول ناموفق بود (اینترنت ضعیف است). ۳ ثانیه صبر می‌کنیم...');
      
      // ۳ ثانیه صبر می‌کنیم
      await new Promise(resolve => setTimeout(resolve, 3000));

      // در این ۳ ثانیه، شاید کاربر خودش دیتا را قطع کرده باشد
      if (!navigator.onLine) {
        setIsOnline(false);
        wasOffline.current = true;
        setIsChecking(false);
        return;
      }

      // ==========================================
      // تلاش دوم (بعد از ۵ ثانیه)
      // ==========================================
      console.log('🔄 تلاش دوم برای اتصال به سرور...');
      isReachable = await performPing();
    }

    // ==========================================
    // نتیجه نهایی
    // ==========================================
    if (isReachable) {
      // متصل است
      setIsOnline(true);
      if (wasOffline.current) {
        wasOffline.current = false;
        refreshBrokenMedia(); // عکس‌ها و ویدیوها را رفرش کن
      }
    } else {
      // هر دو تلاش شکست خورد -> ۱۰۰٪ قطع است
      console.log('❌ اتصال به سرور برقرار نشد. حالت آفلاین فعال شد.');
      setIsOnline(false);
      wasOffline.current = true;
    }

    setIsChecking(false);
  }, []);

  // تابع جادویی: پیدا کردن تمام عکس‌ها و ویدیوهای صفحه و رفرش خودکار آن‌ها
  const refreshBrokenMedia = () => {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      const originalSrc = img.getAttribute('src');
      if (originalSrc && !originalSrc.startsWith('data:')) {
        img.removeAttribute('src'); 
        setTimeout(() => {
          const cacheBuster = originalSrc.includes('?') ? '&' : '?';
          img.setAttribute('src', `${originalSrc}${cacheBuster}retry=${Date.now()}`);
        }, 50);
      }
    });

    const videos = document.querySelectorAll('video');
    videos.forEach(vid => {
      try { vid.load(); } catch (e) {}
    });

    console.log('🔄 رسانه‌های صفحه (عکس‌ها و ویدیوها) پس از اتصال مجدد رفرش شدند.');
  };

  useEffect(() => {
    // وقتی کاربر اینترنت را روشن می‌کند
    const handleOnline = () => {
      console.log('🌐 رویداد روشن شدن اینترنت دریافت شد.');
      checkRealConnection();
    };

    // وقتی کاربر اینترنت را خاموش می‌کند
    const handleOffline = () => {
      console.log('📴 رویداد خاموش شدن اینترنت دریافت شد.');
      setIsOnline(false);
      wasOffline.current = true;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // بررسی اولیه هنگام باز شدن برنامه
    checkRealConnection();

    // بررسی دوره‌ای هر ۳۰ ثانیه (برای اطمینان از پایداری سرور)
    const interval = setInterval(() => {
      if (navigator.onLine) checkRealConnection();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [checkRealConnection]);

  return (
    <NetworkContext.Provider value={{ isOnline, isChecking, checkRealConnection, refreshBrokenMedia }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => useContext(NetworkContext);